import React, { useState } from "react";
import {
  Container,
  Card,
  Button,
  Row,
  Col,
  Tab,
  Tabs,
  Alert,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FaFileInvoiceDollar,
  FaPlus,
  FaDownload,
  FaChartBar,
  FaShieldAlt,
  FaSync,
} from "react-icons/fa";
import Swal from "sweetalert2";

// Hooks personalizados
import { usePlanillas } from "./hooks/usePlanillas";
import { usePlanillasFilters } from "./hooks/usePlanillasFilters";
import { usePlanillasOperations } from "./hooks/usePlanillasOperations";
import { usePlanillasAnalytics } from "./hooks/usePlanillasAnalytics";
import { useSistemaAudit } from "./hooks/useSistemaAudit";

// Componentes separados
import PlanillasTable from "./components/PlanillasTable";
import PlanillasFilters from "./components/PlanillasFilters";
import PlanillasAnalytics from "./components/PlanillasAnalytics";
import PlanillasPagination from "./components/PlanillasPagination";
import SistemaAudit from "./components/SistemaAudit";
import CaliAuditModal from "./components/CaliAuditModal";

/**
 * Componente principal refactorizado para la gestión de planillas
 * Versión optimizada con separación de responsabilidades
 */
const Planillas = ({ usuario }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");

  // Hooks personalizados para lógica de negocio
  const { planillas, loading, error, recargarPlanillas } = usePlanillas(usuario);
  const {
    searchTerm,
    filtroEstado,
    fechaInicioFiltro,
    fechaFinFiltro,
    planillasFiltradas,
    planillasPaginadas,
    totalPages,
    currentPage,
    setSearchTerm,
    setFiltroEstado,
    setFechaInicioFiltro,
    setFechaFinFiltro,
    handlePageChange,
    limpiarFiltros
  } = usePlanillasFilters(planillas);

  const { operacionLoading, procesarPlanilla, aprobarPlanilla, eliminarPlanilla } =
    usePlanillasOperations(planillas, recargarPlanillas);

  const { estadisticas, datosGraficoEstados, datosGraficoTendencias, datosGraficoTipos, datosDesgloseCostos, datosDepartamentos, datosContratos, datosHE, datosAusentismo, datosPulsoDiario, loadingDetalles } =
    usePlanillasAnalytics(planillas, usuario);

  const {
    showSistemaAudit,
    setShowSistemaAudit,
    sistemaAuditTab,
    setSistemaAuditTab,
    metricasSistema,
    loadingMetricas,
    recargarMetricas
  } = useSistemaAudit();

  // Estado para el modal de auditoría Cali
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [planillaAuditar, setPlanillaAuditar] = useState(null);

  const handleIniciarAuditoria = (planillaId) => {
    const planilla = planillas.find(p => p.id === planillaId);
    if (planilla) {
      setPlanillaAuditar(planilla);
      setShowAuditModal(true);
    }
  };

  const handleConfirmarAprobacion = async (planillaId) => {
    setShowAuditModal(false);
    await aprobarPlanilla(planillaId);
  };

  // Función para exportar reportes
  const exportarReporte = async (tipo) => {
    try {
      if (tipo === 'excel') {
        const { utils, writeFile } = await import('xlsx');

        const datosPlanillas = planillasFiltradas.map(planilla => ({
          'Período': planilla.periodo,
          'Fecha Inicio': formatearFecha(planilla.fecha_inicio),
          'Fecha Fin': formatearFecha(planilla.fecha_fin),
          'Estado': planilla.estado,
          'Empleados': planilla.total_empleados || 0,
          'Total Neto': planilla.total_neto || 0,
          'Fecha Creación': formatearFecha(planilla.created_at),
        }));

        const ws = utils.json_to_sheet(datosPlanillas);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Planillas');

        const estadisticasData = [
          { 'Métrica': 'Total Planillas', 'Valor': estadisticas.total },
          { 'Métrica': 'En Borrador', 'Valor': estadisticas.borradores },
          { 'Métrica': 'Procesadas', 'Valor': estadisticas.procesadas },
          { 'Métrica': 'Aprobadas', 'Valor': estadisticas.aprobadas },
          { 'Métrica': 'Total Empleados', 'Valor': estadisticas.totalEmpleados },
          { 'Métrica': 'Total Neto', 'Valor': estadisticas.totalNeto },
        ];
        const wsEstadisticas = utils.json_to_sheet(estadisticasData);
        utils.book_append_sheet(wb, wsEstadisticas, 'Estadísticas');

        writeFile(wb, `reporte_planillas_${new Date().toISOString().split('T')[0]}.xlsx`);
        Swal.fire('Éxito', 'Reporte Excel exportado correctamente', 'success');
      } else {
        const { jsPDF } = await import('jspdf');
        const doc = new jsPDF();

        doc.setFontSize(20);
        doc.text('Reporte de Planillas de Remuneraciones', 20, 30);
        doc.setFontSize(12);
        doc.text(`Generado el: ${new Date().toLocaleDateString('es-PE')}`, 20, 45);

        doc.text('Estadísticas Generales:', 20, 65);
        doc.text(`Total de Planillas: ${estadisticas.total}`, 30, 80);
        doc.text(`Planillas en Borrador: ${estadisticas.borradores}`, 30, 90);
        doc.text(`Planillas Procesadas: ${estadisticas.procesadas}`, 30, 100);
        doc.text(`Planillas Aprobadas: ${estadisticas.aprobadas}`, 30, 110);
        doc.text(`Total Empleados: ${estadisticas.totalEmpleados}`, 30, 120);
        doc.text(`Total Neto Acumulado: S/ ${estadisticas.totalNeto.toFixed(2)}`, 30, 130);

        doc.text('Planillas Recientes:', 20, 150);
        let yPos = 165;
        planillasFiltradas.slice(0, 10).forEach((planilla, index) => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 30;
          }
          doc.text(`${index + 1}. ${planilla.periodo} - ${planilla.estado} - S/ ${planilla.total_neto?.toFixed(2) || '0.00'}`, 30, yPos);
          yPos += 10;
        });

        doc.save(`reporte_planillas_${new Date().toISOString().split('T')[0]}.pdf`);
        Swal.fire('Éxito', 'Reporte PDF exportado correctamente', 'success');
      }
    } catch (error) {
      console.error('Error al exportar reporte:', error);
      Swal.fire('Error', 'No se pudo exportar el reporte', 'error');
    }
  };

  const formatearFecha = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(
      timestamp.seconds ? timestamp.seconds * 1000 : timestamp
    );
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Mostrar loading inicial
  if (loading) {
    return (
      <Container className="py-4">
        <div className="text-center py-5">
          <Spinner animation="border" size="lg" />
          <p className="mt-3">Cargando planillas...</p>
        </div>
      </Container>
    );
  }

  // Mostrar error si existe
  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          <Alert.Heading>Error al cargar planillas</Alert.Heading>
          <p>{error.message}</p>
          <Button variant="outline-danger" onClick={recargarPlanillas}>
            <FaSync className="me-2" />
            Reintentar
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Indicador de carga para operaciones */}
        {operacionLoading && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
            style={{ zIndex: 1040 }}
          >
            <div className="text-center">
              <Spinner
                animation="border"
                variant="light"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="text-white mt-3 mb-0">
                Procesando operación...
              </p>
            </div>
          </div>
        )}

        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium */}
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
              borderBottom: "3px solid #D2691E",
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="fw-bold mb-1">
                  <FaFileInvoiceDollar className="me-3" />
                  Planillas de Remuneraciones
                </h2>
                <p className="mb-0 opacity-75">
                  Gestión completa de planillas de sueldos y beneficios
                </p>
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="light"
                  onClick={() => exportarReporte('excel')}
                  disabled={planillasFiltradas.length === 0}
                  style={{
                    borderRadius: "10px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                  className="d-flex align-items-center gap-2"
                >
                  <FaDownload size={16} />
                  Excel
                </Button>
                <Button
                  variant="light"
                  onClick={() => exportarReporte('pdf')}
                  disabled={planillasFiltradas.length === 0}
                  style={{
                    borderRadius: "10px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                  className="d-flex align-items-center gap-2"
                >
                  <FaDownload size={16} />
                  PDF
                </Button>
                <Button
                  variant="success"
                  onClick={() => navigate('/proceso-planilla')}
                  style={{
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                    border: "none",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  }}
                  className="d-flex align-items-center gap-2"
                >
                  <FaPlus size={16} />
                  Nueva Planilla
                </Button>
              </div>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Contenido principal con pestañas */}
            <Tabs
              activeKey={activeTab}
              onSelect={setActiveTab}
              className="mb-4"
              style={{
                borderBottom: "2px solid #D2691E"
              }}
            >
              {/* Dashboard */}
              <Tab
                eventKey="dashboard"
                title={
                  <span className="d-flex align-items-center gap-2">
                    <FaChartBar />
                    Dashboard
                  </span>
                }
              >
                <Card
                  className="shadow-sm border-0 mt-3"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body className="p-4">
                    <PlanillasAnalytics
                      estadisticas={estadisticas}
                      datosGraficoEstados={datosGraficoEstados}
                      datosGraficoTendencias={datosGraficoTendencias}
                      datosGraficoTipos={datosGraficoTipos}
                      datosDesgloseCostos={datosDesgloseCostos}
                      datosDepartamentos={datosDepartamentos}
                      datosContratos={datosContratos}
                      datosHE={datosHE}
                      datosAusentismo={datosAusentismo}
                      datosPulsoDiario={datosPulsoDiario}
                      loadingDetalles={loadingDetalles}
                    />
                  </Card.Body>
                </Card>
              </Tab>

              {/* Gestión de Planillas */}
              <Tab
                eventKey="planillas"
                title={
                  <span className="d-flex align-items-center gap-2">
                    <FaFileInvoiceDollar />
                    Planillas
                  </span>
                }
              >
                <Card
                  className="shadow-sm border-0 mt-3"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body className="p-4">
                    {/* Filtros */}
                    <PlanillasFilters
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      filtroEstado={filtroEstado}
                      setFiltroEstado={setFiltroEstado}
                      fechaInicioFiltro={fechaInicioFiltro}
                      setFechaInicioFiltro={setFechaInicioFiltro}
                      fechaFinFiltro={fechaFinFiltro}
                      setFechaFinFiltro={setFechaFinFiltro}
                      onLimpiarFiltros={limpiarFiltros}
                    />

                    {/* Tabla de planillas */}
                    <div style={{ overflowX: "auto" }}>
                      <PlanillasTable
                        planillas={planillasPaginadas}
                        onProcesar={procesarPlanilla}
                        onAprobar={aprobarPlanilla}
                        onAuditar={handleIniciarAuditoria}
                        onEliminar={eliminarPlanilla}
                        operacionLoading={operacionLoading}
                      />
                    </div>

                    {/* Paginación */}
                    <PlanillasPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  </Card.Body>
                </Card>
              </Tab>

              {/* Sistema de Auditoría */}
              <Tab
                eventKey="auditoria"
                title={
                  <span className="d-flex align-items-center gap-2">
                    <FaShieldAlt />
                    Auditoría
                  </span>
                }
              >
                <Card
                  className="shadow-sm border-0 mt-3"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body className="p-4">
                    <SistemaAudit
                      sistemaAuditTab={sistemaAuditTab}
                      setSistemaAuditTab={setSistemaAuditTab}
                      metricasSistema={metricasSistema}
                      loadingMetricas={loadingMetricas}
                      onRecargarMetricas={() => recargarMetricas(usuario)}
                      usuario={usuario}
                    />
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>

        {/* Modal de Auditoría Cali */}
        <CaliAuditModal
          show={showAuditModal}
          onHide={() => setShowAuditModal(false)}
          planilla={planillaAuditar}
          usuario={usuario}
          onConfirmarAprobacion={handleConfirmarAprobacion}
        />
      </Container>
    </div>
  );
};

export default Planillas;
